import React from "react";
import useCompanyOrderCards from "./companyOrderCard-hook";
import { IOrderResponse } from "../../../../../../../../Interfaces/responseTypes";
import { OrderStatus } from "../../../../../../../../Interfaces/formValue-interfaces";
import { formatDate } from "../../../../../../../../Utilits/utilits";
import { PhoneIcon } from "../../../../../../../../Components/IconComponents";
import { OrderStatusBadge } from "../OrderStatus/statusBadge";
import useLanguagesHooks from "../../../../../../../../Components/Languages/setLanguage-hooks";
import styles from "./companyOrderCard.module.scss";

interface IProps {
  order: IOrderResponse;
  isNew?: boolean;
  currencySymbol: string;
  onSelect: (order: IOrderResponse) => void;
  onOpenStatusDialog: (order: IOrderResponse) => void;
}

const CompanyOrderCard: React.FC<IProps> = ({
  order,
  isNew,
  currencySymbol,
  onOpenStatusDialog,
  onSelect,
}) => {
  const { t } = useLanguagesHooks();
  const orderParams = JSON.parse(order.order_parameters);
  const { getOrderCardContent } = useCompanyOrderCards(currencySymbol);
  const {
    icon,
    typeLabel,
    typeClass,
    highlight,
    metaFields,
    total,
    itemsCount,
    phone,
    hasNotes,
    notesPreview,
  } = getOrderCardContent(order.order_type, orderParams, order.data);

  const cardClassName = [
    styles.orderCard,
    styles[typeClass],
    isNew ? styles.new : "",
    order.status === OrderStatus.PENDING ? styles.pendingCard : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cardClassName} onClick={() => onSelect(order)}>
      <div className={styles.header}>
        <div className={styles.typeBlock}>
          <div className={styles.iconWrap} aria-hidden>
            {icon}
          </div>
          <div className={styles.typeText}>
            <div className={styles.typeRow}>
              <span className={styles.typeBadge}>{typeLabel}</span>
            </div>
            <p className={styles.orderId}>#{order.id}</p>
            <p className={styles.createdAt}>{formatDate(order.created_at!)}</p>
          </div>
        </div>

        <div className={isNew ? styles.newStatusBox : styles.statusBox}>
          <OrderStatusBadge
            status={order.status}
            onClick={
              order.status !== OrderStatus.NEW
                ? () => onOpenStatusDialog(order)
                : undefined
            }
          />
        </div>
      </div>

      {highlight && (
        <div className={styles.highlight}>
          <div className={styles.highlightLabel}>{highlight.label}</div>
          <div className={styles.highlightValue}>{highlight.value}</div>
        </div>
      )}

      {(phone || itemsCount != null || metaFields.length > 0 || total) && (
        <div className={styles.metaGrid}>
          {phone && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t("Visitor.My_Order_Card.Phone").replace(/\s*:\s*$/, "")}</span>
              <span className={`${styles.metaValue} ${styles.phoneValue}`}>
                <PhoneIcon
                  width={13}
                  height={16}
                  color={isNew ? "rgb(190, 190, 190)" : undefined}
                />
                {phone}
              </span>
            </div>
          )}

          {itemsCount != null && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t("Visitor.My_Order_Card.Items").replace(/\s*:\s*$/, "")}</span>
              <span className={styles.metaValue}>{itemsCount}</span>
            </div>
          )}

          {metaFields.map((field) => (
            <div className={styles.metaItem} key={field.key}>
              <span className={styles.metaLabel}>{field.label}</span>
              <span className={styles.metaValue}>{field.value}</span>
            </div>
          ))}

          {total && (
            <div className={`${styles.metaItem} ${styles.totalItem}`}>
              <span className={styles.metaLabel}>{t("Visitor.Total").replace(/\s*:\s*$/, "")}</span>
              <span className={styles.totalValue}>{total}</span>
            </div>
          )}
        </div>
      )}

      {hasNotes && notesPreview && (
        <div className={styles.notesRow}>
          <span className={styles.notesLabel}>
            {t("Visitor.My_Order_Card.Notes").replace(/\s*:\s*$/, "")}
          </span>
          <p className={styles.notesValue}>{notesPreview}</p>
        </div>
      )}
    </div>
  );
};

export default CompanyOrderCard;
